import React from "react";

import { faEllipsis } from "@fortawesome/pro-regular-svg-icons";

import { ICustomQuestionGetResponse } from "@performyard/models";
import {
	Avatar,
	Chip,
	DropDownMenu,
	type DropDownMenuOption,
	type ICellRendererParams,
	Icon,
	type PYColumnDef,
} from "@performyard/py-components";

import Link from "../../../../../../components/Link";
import Typography from "../../../../../../components/Text";
import TruncatedText from "../../../../../../components/Text/TruncatedText";

import { scaleTypeMap } from "../../../../../../api/services";

import EmployeeURL from "../../../../../../utils/URLs/Employee";

import { caseInsensitiveComparator } from "../../EngagementSetupUtils";
import css from "../SurveyQuestions.module.scss";

// --- Exported context type for the grid ---

export interface SurveyQuestionsGridContext {
	handleOptionsDrawer: (key: string, question: ICustomQuestionGetResponse) => void;
	customFactorsEnabled: boolean;
	customFactorsMap: Record<string, string>;
}

// --- Cell Renderers ---

export const TypeCellRenderer = (params: ICellRendererParams<ICustomQuestionGetResponse>) => {
	if (!params.data) return null;
	const { scale = "" } = params.data;
	return (
		<div className="flex size-full min-h-0 items-center justify-start">
			<Typography size="paragraph-3" weight="regular" testID="type-row">
				{scaleTypeMap[scale] || "N/A"}
			</Typography>
		</div>
	);
};

export const ModifiedByCellRenderer = (params: ICellRendererParams<ICustomQuestionGetResponse>) => {
	const employee = params.data?.updated_by_employee;
	if (!employee) {
		return (
			<div className="flex size-full min-h-0 items-center justify-start">
				<Typography size="paragraph-3" weight="regular">
					-
				</Typography>
			</div>
		);
	}
	return (
		<div className="flex size-full min-h-0 items-center justify-start">
			<Link href={EmployeeURL(employee._id)} className={css.userBox}>
				<Avatar size={20} id={String(employee._id)} />
				<TruncatedText
					text={employee.name || ""}
					size="paragraph-3"
					weight="regular"
					maxWidth={110}
					useTooltip="never"
				/>
			</Link>
		</div>
	);
};

export const StatusCellRenderer = (params: ICellRendererParams<ICustomQuestionGetResponse>) => {
	if (!params.data) return null;
	const { enabled = false } = params.data;
	return (
		<div className="flex size-full min-h-0 items-center justify-start">
			<Chip size={24} label={enabled ? "Enabled" : "Disabled"} color={enabled ? "green" : "red"} />
		</div>
	);
};

export const ActionsCellRenderer = (params: ICellRendererParams<ICustomQuestionGetResponse>) => {
	if (!params.data) return null;
	const { handleOptionsDrawer } = params.context as SurveyQuestionsGridContext;
	const { enabled, factor } = params.data;

	let options: DropDownMenuOption[];

	// custom questions can be edited, disabled, or deleted
	if (factor === "Custom") {
		options = [
			{ key: "edit", label: "Edit" },
			enabled ? { key: "disable", label: "Disable" } : { key: "enable", label: "Enable" },
			{ key: "delete", label: "Delete" },
		];
	}
	// built in questions can only be enabled or disabled
	else {
		options = [enabled ? { key: "disable", label: "Disable" } : { key: "enable", label: "Enable" }];
	}

	return (
		<DropDownMenu
			options={options}
			onSelect={(key: string) => handleOptionsDrawer(key, params.data!)}
			placement="bottom-end"
			minWidth={80}
		>
			<Icon icon={faEllipsis} size={24} color="neutral-5" />
		</DropDownMenu>
	);
};

// --- Column Definitions ---

const getSurveyQuestionsTableCols = (): PYColumnDef<ICustomQuestionGetResponse>[] => [
	{
		headerName: "Factor",
		field: "factor",
		type: "text",
		valueGetter: (params) => {
			if (!params.data) return "";
			const { customFactorsEnabled, customFactorsMap } = params.context as SurveyQuestionsGridContext;
			const factor = params.data.factor || "";
			if (customFactorsEnabled && factor === "Custom") {
				const customFactorId = (params.data as unknown as { custom_factor?: string | null }).custom_factor;
				return (customFactorId && customFactorsMap[customFactorId]) || "Unassigned";
			}
			return factor;
		},
		sortable: true,
		width: 120,
		resizable: false,
		comparator: caseInsensitiveComparator,
	},
	{
		headerName: "Question",
		field: "question",
		type: "text",
		sortable: true,
		width: 375,
		resizable: false,
		comparator: caseInsensitiveComparator,
	},
	{
		headerName: "Type",
		field: "scale",
		cellRenderer: TypeCellRenderer,
		sortable: true,
		width: 100,
		resizable: false,
		comparator: (valueA: string, valueB: string) => {
			const a = scaleTypeMap[valueA] || "N/A";
			const b = scaleTypeMap[valueB] || "N/A";
			return caseInsensitiveComparator(a, b);
		},
	},
	{
		headerName: "Date Created",
		field: "created_at",
		type: "date",
		sortable: true,
		width: 115,
		resizable: false,
	},
	{
		headerName: "Last Modified",
		field: "updated_at",
		type: "date",
		sortable: true,
		width: 115,
		resizable: false,
	},
	{
		headerName: "Modified by",
		field: "updated_by_employee.name",
		cellRenderer: ModifiedByCellRenderer,
		sortable: true,
		width: 140,
		resizable: false,
		comparator: caseInsensitiveComparator,
	},
	{
		headerName: "Status",
		field: "enabled",
		cellRenderer: StatusCellRenderer,
		sortable: true,
		width: 85,
		resizable: false,
	},
	{
		headerName: "",
		field: "options",
		cellRenderer: ActionsCellRenderer,
		valueGetter: (params) => params.data?.enabled,
		cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "8px" },
		sortable: false,
		width: 50,
		resizable: false,
	},
];

export default getSurveyQuestionsTableCols;
